import { type Assign, type OptionalKeys, type Overwrite, type RequiredKeys } from 'utility-types';

/* eslint-disable-next-line no-restricted-imports */
import {
  type PopperPlacementType,
  type PopperProps as RootPopperProps,
} from '@mui/material/Popper';

import { type QuantitativeSize } from '../../types/sizes';

export type MuiPopperProps = RootPopperProps;

/**
 * Represents props that can be provided to the {@link Popper} component that affect the behavior
 * of custom modifiers that may be applied to the {@link Popper} as well.  These props are
 * provided when evaluating the behavior of custom modifiers inside of the {@link useModifiers}
 * hook.
 */
export interface CustomModifierApplicableProps extends Pick<MuiPopperProps, 'placement'> {
  /**
   * Defines the maximum height of the {@link Popover}'s content.
   *
   * This value can also be defined via the 'sx' prop on the {@link Popper} component, but if it
   * is provided via the 'sx' prop, the {@link ConstrainMaxHeightToViewport} modifier will not be
   * able to take that value into account when constraining the height of the {@link Popover}'s
   * content to the available space in the viewport.
   *
   * This value affects the behavior of the {@link ConstrainMaxHeightToViewport} custom modifier,
   * if that modifier is in fact being used.  When that modifier is used, and this value is not
   * provided, the {@link Popover}'s content will expand to fill the available space between
   * the bottom of the target element and the bottom of the viewport (accounting for any padding
   * or offset specified in the modifier's options).
   *
   * When this value is provided, it will be used as an upper bound on the max height of the
   * {@link Popover}'s content, ensuring that the content does not exceed this value even if there
   * is more available space in the viewport.
   *
   * It is important that the max-height is NOT set on the {@link Paper} element that sometimes
   * sits inside of the {@link Popper}, or any other element that is a direct child of the
   * {@link Popper}, when the {@link ConstrainMaxHeightToViewport} modifier is being used, as that
   * would prevent the modifier from being able to properly constrain the height of the content
   * to the available space in the viewport.
   */
  readonly maxHeight: QuantitativeSize<'px' | 'vh'>;
  /**
   * Defines the offset of the {@link Popover}'s content.  The offset, `[number, number]`, should
   * include both the horizontal offset, `number`, and the vertical offset, `number`.
   *
   * Note: If a custom modifier that contains 'offset' as an option is being used, that custom
   * modifier will overwrite the offset defined by this prop.
   */
  readonly offset: [number, number];
}

export type PopperPlacement = PopperPlacementType;

export type MuiModifier = Readonly<
  Assign<
    Overwrite<NonNullable<RootPopperProps['modifiers']>[number], { name: string }>,
    { isCustom?: never }
  >
>;

export type ModifiersArray<T> = (null | T | undefined)[];

/**
 * Defines the events that a {@link Modifier} provided to a {@link Popper} component can listen to
 * and adjust its behavior accordingly.  For instance, if a modifier is configured to listen for
 * the 'resize' event, it will be re-evaluated whenever the window is resized.
 */
export type PopperResizeListener = 'resize' | 'scroll';

/**
 * Defines how a child modifier, {@link IModifierChild}, of a custom modifier,
 * {@link CustomModifier}, is treated when a previously defined modifier that was not defined as
 * a child of another custom modifier, {@link CustomModifier}, has the same name as the child
 * modifier.
 *
 * When a {@link CustomModifier} is evaluated, it is composed of a series of one or more native,
 * MUI modifiers, {@link MuiModifier}, that are defined as its children.  Each of those children
 * are added to the overall list of modifiers that are applied to the {@link Popper} component.
 *
 * If one of those child modifiers has the same name as a previously defined modifier that was
 * not defined as a child of another custom modifier, the behavior defined by this enum determines
 * how that conflict is resolved.
 *
 * 1. Avoid: The child modifier defined by the custom modifier, {@link CustomModifier}, will be
 *   ignored in the case that a previously defined modifier with the same name exists.
 *
 * 2. Overwrite: The child modifier defined by the custom modifier, {@link CustomModifier}, will
 *   take precedence over the previously defined modifier with the same name.
 */
export enum ModifierConflictResolution {
  Avoid = 'avoid',
  Overwrite = 'overwrite',
}

/**
 * Defines the discrete names of the custom modifiers that can be applied to the {@link Popper}
 * component.
 */
export const CustomModifierName = Object.freeze({
  ConstrainMaxHeightToViewport: 'ConstrainMaxHeightToViewport',
} as const);

/**
 * Represents the discrete name of the custom modifiers that can be applied to the {@link Popper}
 * component.
 */
export type CustomModifierName = (typeof CustomModifierName)[keyof typeof CustomModifierName];

/**
 * Represents the general form of a custom modifier that can be provided to the {@link Popper}
 * as an element of its 'modifiers' prop array.
 *
 * This type should not be used externally to this file.  Instead, the {@link CustomModifier}
 * type should be used to reference specific custom modifiers by name.
 */
type ICustomModifier = {
  /**
   * Marks the modifier as a custom modifier, {@link CustomModifier}, which will be used to
   * determine whether or not the modifier should be processed by the custom modifier handling
   * logic inside of the {@link useModifiers} hook.
   *
   * The definition of the 'name' alone is not sufficient to identify a modifier as a
   * custom modifier, {@link CustomModifier}, since it is possible that a non-custom modifier
   * could coincidentally have the same name as a custom modifier.  Therefore, this property
   * is necessary to explicitly mark a modifier as a custom modifier.s
   */
  readonly isCustom: true;
  readonly isDisabled?: boolean;
  readonly isEnabled?: boolean;
  readonly name: CustomModifierName;
  /**
   * Dynamic options that can be configured for a given {@link CustomModifier} when the modifier
   * is included in the 'modifiers' array that is provided as a prop to the {@link Popper}
   * component.
   *
   * These options vary based on the specific custom modifier being used, and affect the child
   * modifiers, {@link IModifierChild}, that the custom modifier applies to the {@link Popper}
   * component.
   */
  readonly options: Record<string, unknown>;
};

interface ConstrainMaxHeightToViewportModifier extends ICustomModifier {
  readonly name: typeof CustomModifierName.ConstrainMaxHeightToViewport;
  readonly options: Partial<{
    readonly listeners: PopperResizeListener[];
    readonly offset: number;
    readonly padding: number;
  }>;
}

type _CustomModifier = ConstrainMaxHeightToViewportModifier;

/**
 * Represents a distinct, concrete custom modifier that can be provided to the {@link Popper}
 * component as elements of its 'modifiers' prop array.
 */
export type CustomModifier<N extends CustomModifierName = CustomModifierName> = {
  [key in _CustomModifier['name']]: Extract<_CustomModifier, { name: key }>;
}[N];

export const customModifier = <N extends CustomModifierName>(
  name: N,
  configuration: Omit<CustomModifier<N>, 'isCustom' | 'name'>,
): CustomModifier<N> => ({
  ...configuration,
  isCustom: true,
  name,
});

/**
 * Represents a modifier that can be included in the 'modifiers' array provided as a prop to the
 * {@link Popper} component.  This can be either a native MUI modifier, {@link MuiModifier}, or a
 * custom modifier, {@link CustomModifier}.
 */
export type Modifier = CustomModifier | MuiModifier;

export const isCustomModifier = (modifier: Modifier): modifier is CustomModifier =>
  modifier.isCustom === true;

/**
 * Represents static settings that are common to a given child modifier,
 * {@link IModifierChild}, of a custom modifier, {@link CustomModifier}.  These settings
 * affect how the child modifier is treated when it is applied to the {@link Popper} component, and
 * are not dependent on props or state inside of the {@link Popper}.
 */
export interface ChildModifierSettings {
  readonly conflictResolution?: ModifierConflictResolution;
  readonly messageOnConflict?: string;
}

/**
 * Represents a child modifier, {@link IModifierChild}, of a custom modifier,
 * {@link CustomModifier}.
 *
 * A {@link CustomModifier} is not a modifier that can be directly provided to the {@link Popper}
 * underlying component (i.e. MUI's {@link Popper} component).  Instead, a {@link CustomModifier}
 * defines an array of one or more child modifiers, {@link IModifierChild}, that are applied to
 * the {@link Popper} when the {@link CustomModifier} is included in the 'modifiers' array provided
 * as a prop to the {@link Popper} component.
 *
 * In other words, a custom modifier, {@link CustomModifier}, contains configuration, options and
 * settings that are used to construct an array of one or more child modifiers, which are then
 * flattened into the final modifiers that are provided to the underlying MUI {@link Popper}
 * component.
 */
export interface IModifierChild {
  /**
   * A method that logs a conflict between a child modifier, {@link IModifierChild}, and an
   * original native MUI modifier, {@link MuiModifier}, when those two modifiers have the same
   * name and one would overwrite the other.
   */
  logConflict(conflictingModifier: MuiModifier): void;
  logWarning(message: string, context?: Record<string, unknown>): void;
  readonly name: string;
  readonly native: MuiModifier;
  readonly parent: IModifierConfiguration;
  readonly settings: ChildModifierSettings;
}

/**
 * Represents the dynamic options for a given custom modifier, {@link CustomModifier}, with a given
 * name, {@link CustomModifierName}, that can be defined dynamically when the modifier is included
 */
export type CustomModifierOptions<N extends CustomModifierName> = N extends CustomModifierName
  ? CustomModifier<N>['options']
  : never;

type NotUndefined<T> = T extends undefined ? never : T;

export type WithDefaults<T extends Record<string, unknown>, Defaults extends Partial<T>> = {
  [key in Exclude<OptionalKeys<T>, keyof Defaults> & string]?: T[key];
} & {
  [key in OptionalKeys<Defaults> & OptionalKeys<T> & string]?: T[key];
} & {
  [key in OptionalKeys<T> & RequiredKeys<Defaults>]-?: NotUndefined<T[key]>;
};

export type NullableButRequiredCustomModifierApplicableProps = {
  [key in keyof CustomModifierApplicableProps]: CustomModifierApplicableProps[key] | null;
};

/**
 * Represents the interface that must be implemented by any configuration class, {@link
 * ModifierConfiguration}, that defines the behavior of a custom modifier, {@link CustomModifier},
 * that can be applied to the {@link Popper} component.
 *
 * The configuration of a custom modifier, {@link CustomModifier}, is different from the dynamic
 * options that can be provided to a custom modifier when it is included in the 'modifiers' array
 * of the {@link Popper} component.  The configuration defines the static behavior of the custom
 * modifier, while the options define the dynamic behavior of the custom modifier.
 */
export interface IModifierConfiguration<N extends CustomModifierName = CustomModifierName> {
  /**
   * A method that returns the children modifiers, {@link IModifierChild}, for a given
   * custom modifier, {@link CustomModifier}, based on the provided options and props.  These
   * children modifiers, {@link IModifierChild}, will be applied to the {@link Popper}
   * component when the custom modifier, {@link CustomModifier}, associated with this configuration
   * is referenced and processed.
   */
  getChildren(
    modifier: CustomModifier<N>,
    props: NullableButRequiredCustomModifierApplicableProps,
  ): IModifierChild[];
  /**
   * A method that can be used to log discrepancies between the behavior of a {@link CustomModifier}
   * and the props provided to the {@link Popper} component that may affect the behavior of the
   * {@link CustomModifier}.
   */
  logPropCollisions(props: NullableButRequiredCustomModifierApplicableProps): void;
  /**
   * A method that logs a warning message related to the behavior or usage of a
   * {@link CustomModifier}.
   */
  logWarning(message: string, context?: Record<string, unknown>): void;
  readonly name: N;
}

export interface SuspendedPopoverClickAwayContext {
  readonly suspend: () => () => void;
}
